import React from 'react';
import { useField } from 'informed';
import { useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive';

import { Checkbox } from '@nw/omega-design-system';
import { RECEIVE_UPDATES_INFO, WhatsAppInfo } from '@nw/shared/config';

import { WhatsappIcon } from '../../icons/WhatsappIcon';

import {
  CheckBoxAndErrorMessageWrapper,
  CheckBoxHeading,
  CheckBoxLabelContainer,
  LabelIconWrapper,
  LabelText
} from './styledComponent';

interface InformedCheckboxProps {
  className?: string;
  labelClassName?: string;
  field: string;
  label?: string;
  validationSchemaObject?: any;
  onChange?: () => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  isInnerHTML?: boolean;
  children?: any;
  checked?: boolean;
  fieldWrapperStyles?: any;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  hintClassName?: string;
  isMarginBottomZero?: boolean;
  isHidden?: boolean;
  shouldHideReceiveUpdates?: boolean;
  id?: string;
  shouldShowWhatsAppIcon?: boolean;
  shouldShowOnlyLabel?: boolean;
  checkboxClassName?: string;
}

export const InformedCheckbox = (props: InformedCheckboxProps) => {
  const {
    validationSchemaObject,
    label,
    className,
    labelClassName,
    isInnerHTML,
    error,
    field,
    fieldWrapperStyles,
    validateOnChange = true,
    validateOnBlur = false,
    hintClassName,
    isMarginBottomZero,
    isHidden,
    shouldHideReceiveUpdates,
    id,
    shouldShowWhatsAppIcon = true,
    shouldShowOnlyLabel,
    checkboxClassName
  } = props;

  const { t } = useTranslation();

  const { fieldState, fieldApi, render, userProps } = useField({
    ...props,
    validationSchema: validationSchemaObject.fields[field],
    field,
    validateOnBlur,
    validateOnChange,
    error: error
  });
  const { setValue, setTouched } = fieldApi;
  const { error: fieldError, value } = fieldState;
  const { onChange, onBlur, ...rest } = userProps;

  const isWhatsAppInfo = field === WhatsAppInfo;

  const renderCheckboxHeading = (): React.ReactNode => {
    if (shouldHideReceiveUpdates) {
      return null;
    }

    const isReceiveUpdatesInfo = field === RECEIVE_UPDATES_INFO;

    if (isWhatsAppInfo || isReceiveUpdatesInfo) {
      return <CheckBoxHeading>{t('payment.receiveUpdates')}</CheckBoxHeading>;
    }

    return null;
  };

  const renderLabel = (): React.ReactNode => {
    if (isWhatsAppInfo && shouldShowWhatsAppIcon) {
      return (
        <LabelIconWrapper>
          <MediaQuery minWidth={769}>
            <WhatsappIcon />
          </MediaQuery>
          <MediaQuery maxWidth={768}>
            <WhatsappIcon />
          </MediaQuery>
          <LabelText
            className={labelClassName}
            dangerouslySetInnerHTML={{
              __html: label
            }}
          />
        </LabelIconWrapper>
      );
    }
    return (
      <LabelText
        className={labelClassName}
        dangerouslySetInnerHTML={{
          __html: label
        }}
      />
    );
  };

  const renderCheckBox = () => {
    return (
      <Checkbox
        {...rest}
        field={field}
        className={`cursor-pointer flex-shrink-0 ${className}`}
        labelClassName={labelClassName}
        id={id ? id : 'checkbox'}
        isSelected={value}
        onChange={(e) => {
          setValue(e);
          if (onChange) {
            onChange(e);
          }
        }}
        onBlur={(e) => {
          setTouched(true);
          if (onBlur) {
            onBlur(e);
          }
        }}
        validationSchema={validationSchemaObject.fields[field]}
        errorMessage={fieldError}
        hintClassName={hintClassName}
        checkboxClassName={checkboxClassName}
      >
        {isInnerHTML ? (
          props.children ? (
            props.children
          ) : (
            renderLabel()
          )
        ) : (
          <LabelText className={labelClassName}>{label}</LabelText>
        )}
      </Checkbox>
    );
  };

  const renderCheckBoxLabelContainer = () => {
    return (
      <CheckBoxLabelContainer className={labelClassName}>
        {isInnerHTML ? (
          props.children ? (
            props.children
          ) : (
            renderLabel()
          )
        ) : (
          <LabelText className={labelClassName}>{label}</LabelText>
        )}
      </CheckBoxLabelContainer>
    );
  };

  return render(
    <React.Fragment key={field}>
      {renderCheckboxHeading()}
      <CheckBoxAndErrorMessageWrapper
        css={fieldWrapperStyles}
        isHidden={isHidden}
        isMarginBottomZero={isMarginBottomZero}
      >
        {shouldShowOnlyLabel
          ? renderCheckBoxLabelContainer()
          : renderCheckBox()}
      </CheckBoxAndErrorMessageWrapper>
    </React.Fragment>
  );
};
